import React from 'react';
import styled from 'styled-components';

const PageContainer = styled.div`
  margin-top: 80px; /* Account for fixed header */
  position: relative;

  &::before {
    content: '';
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      120deg,
      ${(props) => props.theme.colors.overlay},
      transparent
    );
    pointer-events: none;
    z-index: -1;
  }
`;

const PageContentSection = styled.section`
  padding: ${(props) => props.theme.spacing.xl} 0;
  position: relative;
`;

const SectionTitle = styled.div`
  text-align: center;
  margin-bottom: ${(props) => props.theme.spacing.lg};
  position: relative;

  h2 {
    position: relative;
    display: inline-block;
    padding-bottom: ${(props) => props.theme.spacing.sm};
    font-weight: 700;
    letter-spacing: 0.05em;

    &::after {
      content: '';
      position: absolute;
      bottom: 0;
      left: 50%;
      transform: translateX(-50%);
      width: 80px;
      height: 2px;
      background: linear-gradient(
        to right,
        transparent,
        ${(props) => props.theme.colors.secondary},
        transparent
      );
    }

    &::before {
      content: '"';
      font-family: ${(props) => props.theme.fonts.accent};
      position: absolute;
      top: -20px;
      left: -20px;
      font-size: 3rem;
      color: ${(props) => props.theme.colors.secondary};
      opacity: 0.3;
      line-height: 1;
    }
  }
`;

const ContentBox = styled.div`
  background-color: ${(props) => props.theme.colors.bgLight};
  padding: ${(props) => props.theme.spacing.lg}
    ${(props) => props.theme.spacing.xl};
  box-shadow: ${(props) => props.theme.shadows.large};
  border-radius: 0;
  margin-top: ${(props) => props.theme.spacing.md};
  border: 1px solid rgba(212, 175, 55, 0.1);
  position: relative;

  /* Subtle gold accent on one corner */
  &::before {
    content: '';
    position: absolute;
    top: 0;
    right: 0;
    width: 40px;
    height: 40px;
    background: linear-gradient(
      135deg,
      transparent 50%,
      rgba(212, 175, 55, 0.1) 50%
    );
  }

  h3 {
    font-family: ${(props) => props.theme.fonts.heading};
    margin-top: ${(props) => props.theme.spacing.md};
    margin-bottom: ${(props) => props.theme.spacing.sm};
    color: ${(props) => props.theme.colors.primary};
    padding-bottom: 0.5rem;
    position: relative;

    &::after {
      content: '';
      position: absolute;
      bottom: 0;
      left: 0;
      width: 40px;
      height: 1px;
      background-color: ${(props) => props.theme.colors.secondary};
    }
  }

  p {
    line-height: 1.8;
    letter-spacing: 0.02em;
  }

  ul,
  ol {
    margin-left: ${(props) => props.theme.spacing.md};
    margin-bottom: ${(props) => props.theme.spacing.md};

    li {
      margin-bottom: ${(props) => props.theme.spacing.xs};
      line-height: 1.7;
      position: relative;
    }
  }

  ul {
    list-style: none;

    li {
      padding-left: 1.5rem;

      &::before {
        content: '•';
        color: ${(props) => props.theme.colors.secondary};
        position: absolute;
        left: 0;
        font-weight: bold;
      }
    }
  }

  ol {
    list-style: decimal;
    counter-reset: item;

    li {
      counter-increment: item;
    }
  }

  a {
    color: ${(props) => props.theme.colors.accent};
    position: relative;

    &:after {
      content: '';
      position: absolute;
      bottom: -2px;
      left: 0;
      width: 0;
      height: 1px;
      background-color: ${(props) => props.theme.colors.secondary};
      transition: width ${(props) => props.theme.transitions.medium};
    }

    &:hover:after {
      width: 100%;
    }
  }
`;

const PageLayout = ({ title, children }) => {
  return (
    <PageContainer>
      <PageContentSection>
        <div className="container">
          <SectionTitle>
            <h2>{title}</h2>
          </SectionTitle>
          <ContentBox>{children}</ContentBox>
        </div>
      </PageContentSection>
    </PageContainer>
  );
};

export default PageLayout;
